// Single cart item card — thumbnail, qty +/-, inline note, inline discount.
// For serial-tracked lines: shows attached serial chips + a per-line scan input
// that resolves via the sell-context hook before attaching the serial ID.
import { useState, useRef, useCallback } from "react";
import { Trash2, Minus, Plus, StickyNote, Package, X, ScanLine, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/toast";
import type { OrderItem } from "./use-pos-cart";
import type { ScanResult } from "@/features/inventory/use-scan-resolve";

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0";
  return new Intl.NumberFormat("vi-VN").format(n);
}

interface CartItemCardProps {
  item: OrderItem & { imageUrl?: string };
  onUpdateQty: (qty: number) => void;
  onUpdateDiscount: (discount: string) => void;
  onUpdateNote: (note: string) => void;
  onRemove: () => void;
  loading?: boolean;
  /**
   * Resolver for serial-first scan — required when item.serialTracked is true.
   * Caller provides this from useScanResolve({ context:"sell", orderId }).
   * If absent the serial scan input is hidden even for tracked lines.
   */
  serialResolver?: (q: string) => Promise<ScanResult>;
  /** Called when a validated serial should be attached. Caller owns updateItem. */
  onAttachSerial?: (itemId: string, mergedSerialIds: string[]) => void;
  /** Called when a serial should be detached. Caller owns updateItem. */
  onDetachSerial?: (itemId: string, remainingSerialIds: string[]) => void;
}

// ── SerialArea — compact chip strip + scan input for serial-tracked lines ─────

interface SerialAreaProps {
  item: OrderItem;
  serialResolver: (q: string) => Promise<ScanResult>;
  onAttach: (itemId: string, mergedIds: string[]) => void;
  onDetach: (itemId: string, remainingIds: string[]) => void;
}

function SerialArea({ item, serialResolver, onAttach, onDetach }: SerialAreaProps) {
  const [scanValue, setScanValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const attachedSerials = item.serials ?? [];
  const serialCount = attachedSerials.length;
  const needsMore = serialCount < item.quantity;
  const tooMany = serialCount > item.quantity;

  const handleScanKey = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" || !scanValue.trim() || scanning) return;
      e.preventDefault();
      const q = scanValue.trim();
      setScanning(true);
      try {
        const sr = await serialResolver(q);
        setScanValue("");

        if (sr.kind !== "serial") {
          // variant/none result on a per-line scan means the code doesn't match a serial
          toast.error("Mã quét không phải serial hợp lệ cho sản phẩm này");
          return;
        }

        if (!sr.ok) {
          toast.error(sr.reason ?? "Serial không hợp lệ");
          return;
        }

        // Ensure the resolved serial belongs to this line's variant
        if (sr.variant.id !== item.variantId) {
          toast.error(
            `Serial thuộc sản phẩm khác (${sr.variant.productName} ${sr.variant.sku})`,
          );
          return;
        }

        // Dedup: reject if already attached
        const existingIds = attachedSerials.map((s) => s.id);
        if (existingIds.includes(sr.serial.id)) {
          toast.info("Serial đã gắn vào dòng này");
          return;
        }

        // updateItem REPLACES the full serial set — always send the complete merged array
        const mergedIds = [...existingIds, sr.serial.id];
        onAttach(item.id, mergedIds);
      } finally {
        setScanning(false);
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    },
    [scanValue, scanning, serialResolver, item.variantId, item.id, attachedSerials, onAttach],
  );

  const handleDetach = useCallback(
    (serialId: string) => {
      const remainingIds = attachedSerials
        .map((s) => s.id)
        .filter((id) => id !== serialId);
      onDetach(item.id, remainingIds);
    },
    [attachedSerials, item.id, onDetach],
  );

  return (
    <div className="mt-1.5 space-y-1">
      {/* Serial chips */}
      {attachedSerials.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {attachedSerials.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono"
            >
              {s.serialNo}
              <button
                type="button"
                aria-label={`Gỡ serial ${s.serialNo}`}
                className="hover:text-destructive transition-colors"
                onClick={() => handleDetach(s.id)}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Count indicator */}
      <div
        className={[
          "flex items-center gap-1 text-[10px]",
          tooMany
            ? "text-destructive"
            : needsMore
              ? "text-amber-600"
              : "text-green-600",
        ].join(" ")}
      >
        {(needsMore || tooMany) && <AlertCircle size={10} />}
        {serialCount}/{item.quantity} serial
        {needsMore && ` — còn thiếu ${item.quantity - serialCount}`}
        {tooMany && ` — quá ${serialCount - item.quantity}`}
      </div>

      {/* Scan input (only show when more serials needed or to allow replacement) */}
      <div className="flex items-center gap-1">
        <ScanLine size={12} className="text-muted-foreground shrink-0" />
        <Input
          ref={inputRef}
          value={scanValue}
          onChange={(e) => setScanValue(e.target.value)}
          onKeyDown={handleScanKey}
          placeholder="Quét IMEI serial..."
          disabled={scanning}
          className="h-6 text-xs font-mono flex-1 min-w-0"
          autoComplete="off"
        />
      </div>
    </div>
  );
}

// ── CartItemCard ──────────────────────────────────────────────────────────────

export function CartItemCard({
  item,
  onUpdateQty,
  onUpdateDiscount,
  onUpdateNote,
  onRemove,
  loading,
  serialResolver,
  onAttachSerial,
  onDetachSerial,
}: CartItemCardProps) {
  const [discountEditing, setDiscountEditing] = useState(false);
  const [discountDraft, setDiscountDraft] = useState(item.discount);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(item.note ?? "");

  const handleDiscountEdit = () => {
    setDiscountDraft(item.discount);
    setDiscountEditing(true);
  };

  const commitDiscount = () => {
    setDiscountEditing(false);
    const val = discountDraft.trim();
    if (val !== item.discount) onUpdateDiscount(val);
  };

  const handleDiscountKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitDiscount();
    if (e.key === "Escape") setDiscountEditing(false);
  };

  const handleNoteToggle = () => {
    if (!noteOpen) {
      setNoteDraft(item.note ?? "");
      setNoteOpen(true);
    } else {
      commitNote();
    }
  };

  const commitNote = () => {
    setNoteOpen(false);
    const val = noteDraft.trim();
    if (val !== (item.note ?? "")) onUpdateNote(val);
  };

  const handleNoteKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitNote();
    if (e.key === "Escape") setNoteOpen(false);
  };

  const showSerialArea =
    item.serialTracked && !!serialResolver && !!onAttachSerial && !!onDetachSerial;

  return (
    <div
      className={[
        "flex items-start gap-3 px-3 py-2 border-b hover:bg-muted/30 transition-colors",
        loading ? "opacity-50 pointer-events-none" : "",
      ].join(" ")}
    >
      {/* Thumbnail */}
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          className="h-10 w-10 md:h-12 md:w-12 rounded object-cover shrink-0 mt-0.5"
        />
      ) : (
        <div className="h-10 w-10 md:h-12 md:w-12 rounded bg-muted flex items-center justify-center shrink-0 mt-0.5">
          <Package size={20} className="text-muted-foreground" />
        </div>
      )}

      {/* Info + controls */}
      <div className="flex-1 min-w-0">
        {/* Row 1: name + qty + total + remove */}
        <div className="flex items-center gap-2">
          {/* Name */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{item.productName ?? "—"}</p>
            <p className="text-xs text-muted-foreground truncate">
              {item.sku}
              {/* For non-serial-tracked items show inline SN summary; for tracked ones SerialArea handles it */}
              {!item.serialTracked && item.serials && item.serials.length > 0 && (
                <> | SN: {item.serials.map((s) => s.serialNo).join(", ")}</>
              )}
            </p>
          </div>

          {/* Qty controls */}
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-7 p-0"
              disabled={item.quantity <= 1}
              onClick={() => onUpdateQty(item.quantity - 1)}
              aria-label="Giảm số lượng"
            >
              <Minus size={12} />
            </Button>
            <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 w-7 p-0"
              onClick={() => onUpdateQty(item.quantity + 1)}
              aria-label="Tăng số lượng"
            >
              <Plus size={12} />
            </Button>
          </div>

          {/* Line total */}
          <span className="font-semibold font-mono text-sm shrink-0 w-20 md:w-24 text-right">
            {fmtVND(item.lineTotal)} ₫
          </span>

          {/* Remove */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
            onClick={onRemove}
            aria-label="Xóa sản phẩm"
          >
            <Trash2 size={14} />
          </Button>
        </div>

        {/* Row 2: discount + note toggle */}
        <div className="flex items-center gap-3 mt-1">
          {/* Discount */}
          {discountEditing ? (
            <Input
              type="number"
              min={0}
              autoFocus
              className="w-24 h-6 text-xs text-right"
              value={discountDraft}
              onChange={(e) => setDiscountDraft(e.target.value)}
              onBlur={commitDiscount}
              onKeyDown={handleDiscountKey}
            />
          ) : (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={handleDiscountEdit}
              aria-label="Chỉnh sửa giảm giá"
            >
              Giảm: {fmtVND(item.discount)} ₫
            </button>
          )}

          {/* Note toggle */}
          <button
            className={[
              "flex items-center gap-1 text-xs transition-colors",
              noteOpen || item.note ? "text-amber-600" : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
            onClick={handleNoteToggle}
            aria-label="Ghi chú sản phẩm"
          >
            <StickyNote size={12} />
            {!noteOpen && item.note ? (
              <span className="truncate max-w-[160px]">{item.note}</span>
            ) : (
              <span>Ghi chú</span>
            )}
          </button>
        </div>

        {/* Note input (inline, toggleable) */}
        {noteOpen && (
          <Input
            autoFocus
            className="mt-1 h-7 text-xs"
            placeholder="Màu đen, giao trước 5pm..."
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={commitNote}
            onKeyDown={handleNoteKey}
          />
        )}

        {/* Serial scan area — only for serial-tracked lines when resolver is wired */}
        {showSerialArea && (
          <SerialArea
            item={item}
            serialResolver={serialResolver}
            onAttach={onAttachSerial}
            onDetach={onDetachSerial}
          />
        )}
      </div>
    </div>
  );
}
